/**
 * Имя, под которым скачанный документ ложится на диск.
 *
 * Сохранять под одним лишь заголовком нельзя: в нём нет расширения
 * («Протокол консультации 10.09.2026»), файл ложится голым, и браузер,
 * открыв его, показывает пациенту исходный HTML вместо документа. С
 * загруженными снимками то же — PDF с заголовком «Анализ крови» не
 * открывается ничем.
 *
 * Поэтому расширение берётся из `file_name` — имени файла на сервере, — а
 * заголовок остаётся читаемой частью: пациент ищет документ глазами, и
 * «protocol-7.html» в списке загрузок ему ничего не скажет.
 */

/** Символы, недопустимые в имени файла на Windows и в macOS. */
const FORBIDDEN = /[\\/:*?"<>|]/g;

/**
 * Расширение из имени файла, вместе с точкой. Пустая строка, если его нет.
 *
 * Точка в конце имени расширением не считается: `«файл.»` дал бы имя с
 * двумя точками подряд.
 */
const extensionOf = (fileName: string | null | undefined): string => {
  if (!fileName) return '';
  const dot = fileName.lastIndexOf('.');
  if (dot <= 0 || dot === fileName.length - 1) return '';
  return fileName.slice(dot);
};

/**
 * `title` + расширение серверного файла.
 *
 * Если заголовок уже кончается нужным расширением — второго не добавляем:
 * «Выписка.pdf» не должна стать «Выписка.pdf.pdf». Сравнение без учёта
 * регистра: пациент мог загрузить «СНИМОК.JPG».
 *
 * Когда сервер имени не дал (файл пропал с диска), возвращаем заголовок
 * как есть — это не хуже того, что было, и скачивание не ломается.
 */
export const downloadFileName = (
  title: string,
  fileName: string | null | undefined,
): string => {
  const safeTitle = (title || 'Документ').replace(FORBIDDEN, '_').trim();
  const extension = extensionOf(fileName);

  if (!extension) return safeTitle;
  if (safeTitle.toLowerCase().endsWith(extension.toLowerCase())) return safeTitle;

  return `${safeTitle}${extension}`;
};
